import { useEffect, useState } from 'react';
import {
  pingPrintHelper,
  isHelperVersionOk,
  PRINT_HELPER_VERSION,
  downloadPrintHelperInstaller,
} from '@/services/printHelper';
import { Download, AlertCircle, X } from 'lucide-react';

export default function PrintHelperUpdateModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');

  const checkVersion = async () => {
    // If snoozed in this browser session, don't nag repeatedly
    if (sessionStorage.getItem('snooze_print_helper_update') === '1') {
      return;
    }
    try {
      const ping = await pingPrintHelper(1200);
      if (ping && ping.ok && ping.version) {
        if (!isHelperVersionOk(ping.version, PRINT_HELPER_VERSION)) {
          setCurrentVersion(ping.version);
          setIsOpen(true);
        }
      }
    } catch {
      // Helper not running or connection error — ignore
    }
  };

  useEffect(() => {
    // Check after brief delay on startup
    const timer = setTimeout(checkVersion, 2500);
    // Also re-check when user switches back to this browser tab
    window.addEventListener('focus', checkVersion);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', checkVersion);
    };
  }, []);

  if (!isOpen) return null;

  const handleDownload = () => {
    downloadPrintHelperInstaller();
  };

  const handleDismiss = () => {
    sessionStorage.setItem('snooze_print_helper_update', '1');
    setIsOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-xs">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Print Helper Update Available</h3>
                <p className="text-xs text-blue-100">
                  New version available:{' '}
                  <span className="font-mono font-semibold bg-white/20 px-1.5 py-0.5 rounded">
                    v{PRINT_HELPER_VERSION}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-3.5 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-xs">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-950">
                You are running an older version (v{currentVersion || '1.2.0'})
              </p>
              <p className="mt-0.5 text-amber-800 leading-relaxed">
                To enable <strong>Direct Silent A4 / Laser Printing</strong> (bypassing the browser print preview window for sales invoices and quotations), please update your Print Helper background service.
              </p>
            </div>
          </div>

          <div className="space-y-2.5 text-xs text-slate-600 bg-slate-50 p-4 rounded-xl border border-slate-200">
            <p className="font-semibold text-slate-800">Quick 2-Step Update:</p>
            <div className="flex items-start gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold shrink-0 text-[11px]">
                1
              </span>
              <span>Click <strong>Download Update</strong> below to save the new setup file.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold shrink-0 text-[11px]">
                2
              </span>
              <span>Double-click the downloaded file once. It automatically updates and restarts the background service with zero configuration.</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleDismiss}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition-colors"
          >
            Remind Me Later
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download className="w-4 h-4" />
            <span>Download Update (v{PRINT_HELPER_VERSION})</span>
          </button>
        </div>
      </div>
    </div>
  );
}
